import { KeyboardEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { getCommentOrientation } from '@constants'
import { Vector3 } from 'three'
import {
  getPdfLabelBoxSizeWorld,
  getPdfLabelPaddingWorld,
  PDF_COMMENT_TEXT_WIDTH_SCALE,
  PDF_LABEL_LINE_HEIGHT,
} from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanModelLabels/pdfLabelBox'
import { Html } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import { usePlanLabelPdfMetrics } from '@editorHooks'

// CSS font size of the textarea; the <Html transform> scale maps this to the
// comment label's world-space em size (labelScale) so the editor's glyphs match
// the label's glyphs exactly.
const EDITOR_FONT_PX = 16

// In drei's <Html transform> mode the element's 3D basis is scaled by
// 1 / ((distanceFactor || 10) / 400) = 40 by default, i.e. 40 CSS px == 1 world
// unit before the group `scale` is applied. So world-per-pixel = scale / 40.
const DREI_TRANSFORM_PX_PER_UNIT = 40

// CSS border of the textarea, standing in for the label's border tube.
const EDITOR_BORDER_PX = 1

interface Props {
  comment: Comment3D
  labelScale: number
  onCommit: (text: string) => void
  onCancel: () => void
}

/**
 * In-place text editor that replaces a selected comment's 3D label with an HTML
 * textarea. Rendered with drei's <Html transform> so it shares the comment
 * label's position and orientation (the label lies flat in the world XY plane).
 * Enter commits, Esc resets, Shift+Enter inserts a newline.
 */
const CommentTextEditor = ({ comment, labelScale, onCommit, onCancel }: Props): ReactElement => {
  const [value, setValue] = useState(comment.text)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const invalidate = useThree(state => state.invalidate)
  // Ensures only the first of Enter / Esc / blur finalizes the edit (blur also
  // fires when the textarea unmounts, which would otherwise undo an Esc reset).
  const finalizedRef = useRef(false)

  // Read the live DOM value (the textarea is uncontrolled, see below) so commit
  // always uses the latest text even if a render is mid-flight.
  const commit = (next?: string) => {
    if (finalizedRef.current) return
    finalizedRef.current = true
    onCommit(next ?? textareaRef.current?.value ?? value)
  }
  const cancel = () => {
    if (finalizedRef.current) return
    finalizedRef.current = true
    onCancel()
  }

  const position = useMemo(
    () => new Vector3(comment.position.x, comment.position.y, comment.position.z),
    [comment.position],
  )

  const htmlRotation = useMemo((): [number, number, number] => {
    if (getCommentOrientation(comment) === 'vertical') {
      return [0, 0, Math.PI / 2]
    }
    return [0, 0, 0]
  }, [comment])

  // Focus + select all on open so the user can immediately retype or edit.
  // preventScroll is essential: the textarea sits inside a drei <Html transform>
  // (positioned via a large CSS transform), so a default focus would scroll it
  // into view and visually shift the editor/scene to the wrong spot.
  //
  // The textarea lives in a drei <Html transform> portal that is only laid out
  // on the next rendered frame, so a synchronous focus() on mount can be a
  // no-op (which is why it previously took a second click to enter the field).
  // Focus immediately and retry on the next animation frame to cover both cases.
  useEffect(() => {
    const focusTextarea = () => {
      const textarea = textareaRef.current
      if (!textarea) return
      textarea.focus({ preventScroll: true })
      textarea.select()
    }
    focusTextarea()
    const frame = requestAnimationFrame(focusTextarea)
    invalidate()
    return () => cancelAnimationFrame(frame)
  }, [invalidate])

  // Request a frame whenever the content changes so the transform/size update.
  useEffect(() => {
    invalidate()
  }, [value, invalidate])

  const lines = useMemo(() => (value.length > 0 ? value.split('\n') : ['']), [value])

  // Match the Comment3D / Text3DThree box exactly: same background width (PDF
  // box paddings + width scaling) and the same padding between text block and
  // box edge, mapped from world units to CSS px via the <Html transform> scale
  // used below. The line height is the PDF label's, so the textarea's rows
  // stack like the label's lines and the box heights agree as well.
  const pdfMetrics = usePlanLabelPdfMetrics()
  const { editorWidthPx, editorPaddingPx } = useMemo(() => {
    const paddingWorld = getPdfLabelPaddingWorld(pdfMetrics)
    const rectangleWidthWorld = getPdfLabelBoxSizeWorld(
      lines,
      pdfMetrics,
      true,
      PDF_COMMENT_TEXT_WIDTH_SCALE,
    ).width
    const worldPerCssPx = labelScale / EDITOR_FONT_PX
    return {
      editorWidthPx: rectangleWidthWorld / worldPerCssPx,
      // border-box: the CSS border is part of the label's padding band
      editorPaddingPx: Math.max(0, paddingWorld / worldPerCssPx - EDITOR_BORDER_PX),
    }
  }, [lines, labelScale, pdfMetrics])

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Keep typing from triggering scene shortcuts.
    event.stopPropagation()
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      commit()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancel()
    }
  }

  return (
    <Html
      transform
      position={position}
      rotation={htmlRotation}
      scale={(labelScale * DREI_TRANSFORM_PX_PER_UNIT) / EDITOR_FONT_PX}
      zIndexRange={[100, 0]}
      style={{ pointerEvents: 'auto' }}
    >
      <textarea
        ref={textareaRef}
        // Uncontrolled: a controlled value gets re-applied by drei's frequent
        // <Html> re-renders, which resets the caret to the end while typing in
        // the middle. We mirror changes into state only for sizing/commit.
        defaultValue={comment.text}
        rows={lines.length}
        onChange={event => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onPointerDown={event => event.stopPropagation()}
        onBlur={() => commit()}
        data-cy="comment-text-editor"
        style={{
          color: comment.color,
          background: comment.backgroundColor ?? '#FFFFFF',
          border: `${EDITOR_BORDER_PX}px solid ${comment.color}`,
          borderRadius: 0,
          padding: editorPaddingPx,
          textAlign: 'center',
          // the label is typeset in Arial (and its box width is measured with
          // Arial glyph metrics), so the editor text has to be Arial as well
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: EDITOR_FONT_PX,
          lineHeight: PDF_LABEL_LINE_HEIGHT,
          boxSizing: 'border-box',
          width: editorWidthPx,
          resize: 'none',
          outline: 'none',
          overflow: 'hidden',
          whiteSpace: 'pre',
        }}
      />
    </Html>
  )
}

export default CommentTextEditor
